import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Table, BaseChart, isNumber, ratio, percent, number, innings } from 'yankees-react-utils';
import { get } from 'dot-prop-immutable';
import ReportNote from '../../../../components/ReportNote';

const StyledStealingAgainstTableLabel = styled.div`
    font-weight: bold;
    text-align: center;
    white-space: nowrap;
`;
const StyledByCountsLabel = styled(StyledStealingAgainstTableLabel)`
    ${({ $vsrhh }) => ($vsrhh ? 'color: #0070C0;' : '')}
    ${({ $vslhh }) => ($vslhh ? 'color: #CC0000;' : '')}
`;
const StyledTableTotal = styled.td`
    font-weight: bold;
`;
const StealingAgainstPitchers = ({ data, levelid }) => {
    const { t: translate } = useTranslation();
    const counts = ['00', '01', '02', '10', '11', '12', '20', '21', '22', '30', '31', '32'];
    return !!data && !!data.pitchers && (
        <Table>
            {data.pitchers.filter((p) => data.pitcherObj[p.PlayerID]).map((p) => {
                const pitcherData = data.pitcherObj[p.PlayerID];
                const pitcherAvgs = data.pitcherAvgs[p.PlayerID];
                const pitcherCounts = data.playerCounts[p.PlayerID];
                const pitchTimesChart = data.pitchTimesChartConfig[p.PlayerID];
                const setTimesChart = data.setTimesChartConfig[p.PlayerID];

                    const pitchInDirtTot = ratio(pitcherCounts?.pitchindirt, pitcherCounts?.pitches);
                    const pitcherPickoffs = data.pitcherpkof.find((pk) => pk.playerid === p.PlayerID) || {};
                    const pkoTot = pitcherPickoffs?.po1b + pitcherPickoffs?.po2b + pitcherPickoffs?.po3b;
                    const fbRhbTot = ratio(pitcherCounts?.fbrhb, pitcherCounts?.pitchesrhb);
                    const fbLhbTot = ratio(pitcherCounts?.fblhb, pitcherCounts?.pitcheslhb);
                    return (
                        <tbody key={p.PlayerID} className="border-bottom border-bottom-2">
                            <tr>
                                <th rowSpan={6}>
                                    <StyledStealingAgainstTableLabel>
                                        {p.UsesName}<br />{p.LastName} {p.Number}
                                    </StyledStealingAgainstTableLabel>
                                    <StyledStealingAgainstTableLabel className="pt-1">
                                        <small><b>{translate('baseball.abbr-inning-pitched')}:</b> {innings(pitcherData.outs)}</small>
                                    </StyledStealingAgainstTableLabel>
                                    <StyledStealingAgainstTableLabel>
                                        <small><b>{translate('sections.opponent.team.pit-in-dirt')}:</b> {pitcherData.pitchindirt}</small>
                                    </StyledStealingAgainstTableLabel>
                                    <StyledStealingAgainstTableLabel>
                                        <small><b>{translate('sections.opponent.team.abbr-pko')} {translate('sections.opponent.team.abbr-att')}:</b> {pitcherData.po1b + pitcherData.po2b + pitcherData.po3b}</small>
                                    </StyledStealingAgainstTableLabel>
                                    {isNumber(pitcherAvgs?.r1min, pitcherAvgs?.r1max) && (
                                        <StyledStealingAgainstTableLabel>
                                            <small><b>{translate('sections.opponent.team.r1-brk')}:</b> {number(pitcherAvgs.r1min, 2)} - {number(pitcherAvgs.r1max, 2)}</small>
                                        </StyledStealingAgainstTableLabel>
                                    )}
                                    {isNumber(pitcherAvgs?.r2min, pitcherAvgs?.r2max) && (
                                        <StyledStealingAgainstTableLabel>
                                            <small><b>{translate('sections.opponent.team.r2-brk')}:</b> {number(pitcherAvgs.r2min, 2)} - {number(pitcherAvgs.r2max, 2)}</small>
                                        </StyledStealingAgainstTableLabel>
                                    )}
                                    {isNumber(pitcherAvgs?.r1fbmin, pitcherAvgs?.r1fbmax) && (
                                        <StyledStealingAgainstTableLabel>
                                            <small><b>FB/CT {translate('sections.opponent.team.r1-brk')}:</b> {number(pitcherAvgs.r1fbmin, 2)} - {number(pitcherAvgs.r1fbmax, 2)}</small>
                                        </StyledStealingAgainstTableLabel>
                                    )}
                                    {isNumber(pitcherAvgs?.r2fbmin, pitcherAvgs?.r2fbmax) && (
                                        <StyledStealingAgainstTableLabel>
                                            <small><b>FB/CT {translate('sections.opponent.team.r2-brk')}:</b> {number(pitcherAvgs.r2fbmin, 2)} - {number(pitcherAvgs.r2fbmax, 2)}</small>
                                        </StyledStealingAgainstTableLabel>
                                    )}
                                    {isNumber(pitcherAvgs?.r1bbmin, pitcherAvgs?.r1bbmax) && (
                                        <StyledStealingAgainstTableLabel>
                                            <small><b>CB/SL {translate('sections.opponent.team.r1-brk')}:</b> {number(pitcherAvgs.r1bbmin, 2)} - {number(pitcherAvgs.r1bbmax, 2)}</small>
                                        </StyledStealingAgainstTableLabel>
                                    )}
                                    {isNumber(pitcherAvgs?.r2bbmin, pitcherAvgs?.r2bbmax) && (
                                        <StyledStealingAgainstTableLabel>
                                            <small><b>CB/SL {translate('sections.opponent.team.r2-brk')}:</b> {number(pitcherAvgs.r2bbmin, 2)} - {number(pitcherAvgs.r2bbmax, 2)}</small>
                                        </StyledStealingAgainstTableLabel>
                                    )}
                                </th>
                                <th />
                                {counts.map((c) => <th key={c}>{c.split('').join('-') || 'Tot.'}</th>)}
                                <th>Tot.</th>
                                <td rowSpan={6} style={{ backgroundColor: 'white' }}>
                                    {!!pitchTimesChart && (<BaseChart options={pitchTimesChart} />)}
                                    {!!setTimesChart && (<BaseChart options={setTimesChart} />)}
                                </td>
                            </tr>
                            <tr>
                                <td><StyledByCountsLabel>{translate('sections.opponent.team.pit-in-dirt')}</StyledByCountsLabel></td>
                                {counts.map((c) => {
                                    const pInDirt = get(data.playerCounts, `${p.PlayerID}.pitchindirt${c}`);
                                    const pCount = get(data.playerCounts, `${p.PlayerID}.pitch${c}`);
                                    const val = ratio(pInDirt, pCount);
                                    return (
                                        <td key={c} className={val >= 0.1 ? 'stoplightgreen' : 'stoplightnone'}>
                                            {isNumber(val) ? percent(val) : '--'}
                                        </td>
                                    );
                                })}
                                <StyledTableTotal className={pitchInDirtTot > 0.1 ? 'stoplightgreen' : 'stoplightnone'}>
                                    {isNumber(pitchInDirtTot) ? percent(pitchInDirtTot) : '--'}
                                </StyledTableTotal>
                            </tr>
                            <tr className="border-bottom border-bottom-2">
                                <td><StyledByCountsLabel>{translate('sections.opponent.team.abbr-pko')} {translate('sections.opponent.team.abbr-att')}</StyledByCountsLabel></td>
                                {counts.map((c) => {
                                    const pPko = get(pitcherPickoffs, `po${c}`);
                                    const pCount = get(data.playerCounts, `${p.PlayerID}.pkopitch${c}`);
                                    return (
                                        <td key={c} className={ratio(pPko, pCount) >= 0.18 ? 'stoplightyellow' : 'stoplightnone'}>
                                            {pPko || 0}
                                        </td>
                                    );
                                })}
                                <StyledTableTotal className={ratio(pkoTot, pitcherCounts?.pkopitches) >= 0.18 ? 'stoplightyellow' : 'stoplightnone'}>
                                    {pkoTot || 0}
                                </StyledTableTotal>
                            </tr>
                            <tr>
                                <td><StyledByCountsLabel $vsrhh>FB/CT {translate('baseball.abbr-versus')} {translate('baseball.rhh')}</StyledByCountsLabel></td>
                                {counts.map((c) => {
                                    const pFbRhb = get(data.playerCounts, `${p.PlayerID}.fbrhb${c}`);
                                    const pCount = get(data.playerCounts, `${p.PlayerID}.pitchesrhb${c}`);
                                    const val = ratio(pFbRhb, pCount);
                                    return (<td key={c}>{isNumber(val) ? percent(val) : '--'}</td>);
                                })}
                                <StyledTableTotal>{isNumber(fbRhbTot) ? percent(fbRhbTot) : '--'}</StyledTableTotal>
                            </tr>
                            <tr className="border-bottom">
                                <td><StyledByCountsLabel $vslhh>FB/CT {translate('baseball.abbr-versus')} {translate('baseball.lhh')}</StyledByCountsLabel></td>
                                {counts.map((c) => {
                                    const pFbLhb = get(data.playerCounts, `${p.PlayerID}.fblhb${c}`);
                                    const pCount = get(data.playerCounts, `${p.PlayerID}.pitcheslhb${c}`);
                                    const val = ratio(pFbLhb, pCount);
                                    return (<td key={c}>{isNumber(val) ? percent(val) : '--'}</td>);
                                })}
                                <StyledTableTotal>{isNumber(fbLhbTot) ? percent(fbLhbTot) : '--'}</StyledTableTotal>
                            </tr>
                            <tr>
                                <td colSpan={14}>
                                    <ReportNote playerId={p.PlayerID} type="brkt" levelId={levelid} section={30} />
                                </td>
                            </tr>
                        </tbody>
                    );
                })}
        </Table>
    );
};

StealingAgainstPitchers.propTypes = {
    levelid: PropTypes.number.isRequired,
    data: PropTypes.shape({
        pitchers: PropTypes.arrayOf(PropTypes.object),
        reportPitchers: PropTypes.arrayOf(PropTypes.object),
        pitcherObj: PropTypes.shape({ playerid: PropTypes.number }),
        pitcherAvgs: PropTypes.shape({ playerid: PropTypes.number }),
        playerCounts: PropTypes.shape({ playerid: PropTypes.number }),
        bycount: PropTypes.arrayOf(PropTypes.object),
        pitchTimesChartConfig: PropTypes.shape({ id: PropTypes.number }),
        setTimesChartConfig: PropTypes.shape({ id: PropTypes.number }),
        pitcherpkof: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
};

export default StealingAgainstPitchers;
